// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// How wide the window is, as React state (M3-37).
//
// The navigation bar's shape is a function of width (08 §3: five tabs or three, bottom bar or
// header), and that function lives in `navigation.ts` where it has tests. This is the one line of
// browser it needs.
//
// A hook rather than a media query in the stylesheet because the *set of tabs* changes, not just
// their layout: rendering five and hiding two would put two disabled buttons in the accessibility
// tree of every phone, and a screen-reader user would be told about destinations that are not
// there.

import { useEffect, useState, useSyncExternalStore } from 'react'

/** Server and node have no window; a desktop-ish default is the least wrong answer. */
export const ASSUMED_WIDTH = 1024

function subscribe(onChange: () => void): () => void {
  const target = globalThis as { addEventListener?: typeof window.addEventListener; removeEventListener?: typeof window.removeEventListener }
  target.addEventListener?.('resize', onChange)
  // Rotating a phone fires `resize`, but on iOS Safari it fires *before* the new dimensions are
  // readable, so the first answer after a rotation is the old one. `orientationchange` lands
  // after, and subscribing to both costs one extra render on a device that rotates.
  target.addEventListener?.('orientationchange', onChange)
  return () => {
    target.removeEventListener?.('resize', onChange)
    target.removeEventListener?.('orientationchange', onChange)
  }
}

function currentWidth(): number {
  const width = (globalThis as { innerWidth?: number }).innerWidth
  return typeof width === 'number' && width > 0 ? width : ASSUMED_WIDTH
}

/** The viewport width in CSS pixels, kept current. */
export function useViewportWidth(): number {
  return useSyncExternalStore(subscribe, currentWidth, () => ASSUMED_WIDTH)
}

/**
 * The size of one element, kept current.
 *
 * The *viewport* is not what the machine is laid out in: the shell has a top bar and, on a phone,
 * a navigation bar, so the stage is shorter than the window by an amount that depends on both.
 * Anything that has to agree with where a finger lands has to measure the element.
 *
 * A `ResizeObserver` rather than the resize event: the stage also changes size when the
 * navigation bar switches from a bottom bar to a header, which is a layout change with no window
 * resize behind it at all.
 */
export function useElementSize(element: Element | null): { width: number; height: number } {
  const [size, setSize] = useState({ width: 0, height: 0 })
  useEffect(() => {
    if (element === null) return
    const measure = (): void => {
      const box = element.getBoundingClientRect()
      setSize((was) =>
        was.width === box.width && was.height === box.height
          ? was
          : { width: box.width, height: box.height },
      )
    }
    measure()
    const Observer = (globalThis as { ResizeObserver?: typeof ResizeObserver }).ResizeObserver
    if (Observer === undefined) return
    const observer = new Observer(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [element])
  return size
}
